import {
  equal,
  getQueryTransformation,
  like,
  nested,
  notEqual,
} from '@/api/helpers';
import { CourseSchedule, MaterialType } from '@/client/courses';
import { FilterNamesEnum, getFiltersFromColumns } from '@/client/helpers';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import {
  DataTable,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useAccountSchedules } from '@/hooks/query';
import { useQueryParams } from '@/hooks/query.hook';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import DatatableThumbnail from '@/ui/datatable-thumbnail/DatatableThumbnail';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { coursesEnrollToOptions, getScheduleEntity } from '@/utils/helpers';
import { displayMiltipleItems } from '@/utils/helpers/ui.helper';
import { getScheduleEnrollDate } from '@/utils/planner';
import { debounce } from 'lodash';
import { default as moment } from 'moment';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import React, { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AccountEnrollmentActionHeader } from './AccountEnrollmentActionHeader';
import { EnrollmentTabs } from './EnrollmentTabs';

export const AccountCourseSchedulePage: React.FC<{ isMaterial?: boolean }> = ({
  isMaterial,
}) => {
  const { t } = useTranslation();
  const { user } = useAppSelector(selectCurrentUserState);
  const account = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();

  const { skip, take, sort, apiFilters, onFilter, onSort, onPage, setSkip } =
    useTable({ tableName: TableNamesEnum.COURSES_ACCOUNT_SCHEDULE });

  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 300),
    [],
  );

  const {
    isLoading: isScheduleLoading,
    schedules,
    refetch,
  } = useAccountSchedules(
    {
      take,
      skip,
      filters: [
        ...apiFilters,
        ...(multiSearchValue.length
          ? [nested('course', [like('name', multiSearchValue)])]
          : []),
        nested(
          'course',
          isMaterial
            ? [notEqual('type', MaterialType.COURSE)]
            : [equal('type', MaterialType.COURSE)],
        ),
      ],
      sort:
        sort && sort.length > 0 && sort[0] !== 'created'
          ? [sort.join(',')]
          : ['date,asc'],
      enabled: true,
    },
    account?.id,
  );

  const columns: DataTableColumnType[] = [
    {
      field: 'content.name',
      header: isMaterial ? t('material') : t('content'),
      sortable: true,
      filterable: false,
      style: {
        minWidth: '200px',
        maxWidth: '300px',
      },
      render: (row: CourseSchedule) => (
        <FlexContainer gap={16}>
          {row?.course?.thumbnailUrl && (
            <DatatableThumbnail
              className={
                moment(row?.course?.releaseDate).isAfter(moment(), 'day')
                  ? 'upcoming-course'
                  : ''
              }
              url={row.course.thumbnailUrl}
            />
          )}
          <FlexContainer
            direction="column"
            gap={8}
            className="align-self-center"
          >
            {row?.course?.name}
          </FlexContainer>
        </FlexContainer>
      ),
    },
    {
      field: 'date',
      header: t('generic.enrollDate'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.byDate'),
        field: FilterNamesEnum.SCHEDULE_UPCOMING_DATE,
        type: FilterTypeEnum.SELECT_UPCOMING_PAST,
        placeholder: t('filter.date'),
      },
      className: 'no-padding',
      render: (row: CourseSchedule) =>
        row?.date ? (
          <FormatDate
            format={DateFormats.TIMEDATE}
            date={getScheduleEnrollDate(row)}
          />
        ) : (
          <div>&#8212;</div>
        ),
    },
    {
      field: 'unEnrolledAt',
      header: t('generic.unEnrolledAt'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.unEnrolled'),
        field: FilterNamesEnum.SCHEDULE_UNENROLLED,
        type: FilterTypeEnum.SELECT_YES_NO,
        placeholder: t('filter.unEnrolled'),
      },
      className: 'no-padding',
      render: (row: CourseSchedule) =>
        row?.unEnrolledAt ? (
          <FormatDate format={DateFormats.TIMEDATE} date={row.unEnrolledAt} />
        ) : (
          <div>&#8212;</div>
        ),
    },
    ...(account?.courseDueDays
      ? [
          {
            field: 'dueDate',
            header: t('generic.dueDate'),
            sortable: true,
            filterable: false,
            className: 'no-padding',
            render: (row: CourseSchedule) =>
              row?.dueDate && !row.unEnrolledAt ? (
                <FormatDate format={DateFormats.TIMEDATE} date={row.dueDate} />
              ) : (
                <div>&#8212;</div>
              ),
          } as DataTableColumnType,
        ]
      : []),
    {
      field: 'entity.name',
      header: t('generic.enrollingTo'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.ENTITY_NAME,
        type: FilterTypeEnum.TEXT,
      },
      className: 'no-padding',
      render: (row: CourseSchedule) =>
        row?.entity?.name ? (
          displayMiltipleItems(row.entity.name, t)
        ) : (
          <div>&#8212;</div>
        ),
    },
    {
      field: 'entity.type',
      header: t('generic.entity'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.ENTITY_TYPE,
        type: FilterTypeEnum.MULTI_SELECT,
        options: coursesEnrollToOptions(t, user, account, true),
      },
      className: 'no-padding',
      render: (row: CourseSchedule) =>
        row?.entity.type ? (
          getScheduleEntity(row.entity.type)
        ) : (
          <div>&#8212;</div>
        ),
    },
    {
      field: 'usersCount',
      header: t('users'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: CourseSchedule) => (row?.usersCount ? row.usersCount : 0),
    },
    {
      field: 'scheduledBy.name',
      header: t('generic.scheduledBy'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.SCHEDULED_BY,
        type: FilterTypeEnum.TEXT,
      },
      className: 'no-padding',
      render: (row: CourseSchedule) =>
        row?.scheduledBy.id ? row.scheduledBy?.name : <div>&#8212;</div>,
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns.map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  //

  const { params } = useQueryParams(getQueryTransformation<{ name: string }>());

  const toolbar = (
    <DataTableToolbar justify="space-between">
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
          defaultValues={
            !params.filters
              ? { [FilterNamesEnum.SCHEDULE_UPCOMING_DATE]: 'yes' }
              : params.filters
          }
          tableName={
            isMaterial
              ? TableNamesEnum.MATERIAL_ACCOUNT_SCHEDULE
              : TableNamesEnum.COURSES_ACCOUNT_SCHEDULE
          }
          className="flex-initial"
        />
        <div className="p-input-icon-left flex-auto min-w-300">
          <InputText
            className="w-full"
            onInput={debouncedSetMultiSearchValue}
            placeholder={
              isMaterial ? t('material.search.name') : t('content.search.name')
            }
            autoComplete="off"
          />
          <i className="pi pi-search" />
        </div>
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={
          isMaterial
            ? TableNamesEnum.MATERIAL_ACCOUNT_SCHEDULE
            : TableNamesEnum.COURSES_ACCOUNT_SCHEDULE
        }
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  const onRowClick = (e: DataTableRowClickEvent) => {
    if (e?.data?.course?.id) {
      navigate(
        RedirectPaths[
          isMaterial
            ? e?.data?.course?.type === MaterialType.RESOURCE
              ? RedirectPathsEnum.RESOURCES_EDIT
              : RedirectPathsEnum.CUSTOM_MATERIALS_EDIT
            : RedirectPathsEnum.COURSES_CONTENT
        ](e.data.course.id),
      );
    }
  };

  return (
    <>
      {account && (
        <>
          <AccountEnrollmentActionHeader
            account={account}
            onTriggerRefetch={refetch}
            isMaterial={isMaterial}
          />
          <EnrollmentTabs />
          <DataTable
            dataKey="dataId"
            rowGroupMode="rowspan"
            groupRowsBy="course.name"
            data={schedules?.result.map((x, i) => ({
              ...x,
              dataId: `${x.entity.id}-${x.course?.id}-${i}`,
            }))}
            count={schedules?.count as number}
            isLoading={isScheduleLoading}
            toolbar={toolbar}
            columns={columns}
            visibleColumns={visibleColumns}
            onPage={onPage}
            rows={take}
            onRowClick={onRowClick}
            skip={skip}
            onSort={onSort}
            sort={sort}
            classNames="rowspan-datatable with-actions"
          />
        </>
      )}
    </>
  );
};
